import { ReactNode } from "react";
import { Box, styled } from "@mui/material";
import { tokens } from "@styles/tokens";

interface FormFooterProps {
  onCancel: () => void;
  onSubmit: () => void;
  /** Primary button label, e.g. "Save Stock". */
  submitLabel: string;
  /** Optional leading icon for the primary button. */
  submitIcon?: ReactNode;
  cancelLabel?: string;
  submitting?: boolean;
  disabled?: boolean;
}

/**
 * Sticky form footer: a ghost Cancel and a primary submit button. Shared across
 * the redesigned drawer forms so the action bar is identical everywhere; safe-
 * area inset keeps the buttons clear of the iOS home indicator.
 */
export const FormFooter = ({
  onCancel,
  onSubmit,
  submitLabel,
  submitIcon,
  cancelLabel = "Cancel",
  submitting,
  disabled,
}: FormFooterProps) => (
  <Bar>
    <Cancel
      type="button"
      data-testid="form-cancel"
      onClick={onCancel}
      disabled={submitting}
    >
      {cancelLabel}
    </Cancel>
    <Submit
      type="button"
      data-testid="form-submit"
      onClick={onSubmit}
      disabled={submitting || disabled}
    >
      {submitIcon}
      {submitLabel}
    </Submit>
  </Bar>
);

const Bar = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: tokens.space(3),
  flexShrink: 0,
  padding: `${tokens.space(3)}px ${tokens.space(4)}px`,
  paddingBottom: `calc(env(safe-area-inset-bottom) + ${tokens.space(3)}px)`,
  borderTop: `1px solid ${theme.skuey.border}`,
  backgroundColor: theme.skuey.surface,
}));

const Base = styled("button")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: tokens.space(2),
  height: 48,
  borderRadius: tokens.radius.md,
  fontFamily: tokens.font.family,
  fontSize: tokens.font.size.body[0],
  fontWeight: tokens.font.weight.semibold,
  cursor: "pointer",
  transition: "background-color .12s ease",
  "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
});

const Cancel = styled(Base)(({ theme }) => ({
  flex: 1,
  border: `1px solid ${theme.skuey.border}`,
  backgroundColor: theme.skuey.surface,
  color: theme.skuey.body,
  "&:hover": { backgroundColor: theme.skuey.surfaceAlt },
}));

const Submit = styled(Base)(({ theme }) => ({
  flex: 2,
  border: "none",
  backgroundColor: theme.skuey.brand[600],
  color: theme.skuey.white,
  "&:hover": { backgroundColor: theme.skuey.brand[700] },
}));
